/* eslint-disable sort-keys-fix/sort-keys-fix -- CSS-in-JS pseudo-selector keys use semantic ordering */
// MUI v4 Chip — NS/Tractie component overrides.
//
// Backstage core (CatalogTable default columns: lifecycle, metadata.tags, etc.)
// renders descriptive labels via MUI v4 `<Chip>`. Those are TAGS, not selection
// chips. We map MuiChip -> Tractie Tag spec (1em pill, alpha-default bg,
// inverse text, 12px font, 2px/8px padding) per @tractie/components
// tag.css.js:30-43.
//
// transition: none op root + clickable surfaces (ground-rules.md "Motion and text": geen animatie).

export const MuiChipLightOverrides = {
  root: {
    fontFamily: 'inherit',
    fontSize: '0.75rem',
    lineHeight: 1.5,
    display: 'inline-flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    height: 'auto',
    color: 'var(--ns-color-fg-default-inverse)',
    backgroundColor: 'var(--ns-color-bg-alpha-default)',
    // Tractie tag spec: --tractie-tag-border-radius: 1em, declared on :host
    // (custom-property em resolves at the declaring element's font-size = 16px).
    // For non-shadow MUI surface we replicate the emergent 16px directly.
    borderRadius: '16px',
    whiteSpace: 'nowrap' as const,
    cursor: 'default' as const,
    outline: 0,
    textDecoration: 'none' as const,
    border: 'none',
    padding: '2px 8px',
    verticalAlign: 'middle' as const,
    boxSizing: 'border-box' as const,
    transition: 'none',
    '&$disabled': {
      opacity: 0.5,
      pointerEvents: 'none' as const,
    },
    // ────────────────────────────────────────────────────────────────────
    // Cross-cluster X4: Tractie color-bridge for MUI Chip-as-Tag.
    //
    // Tractie tag-spec (`@tractie/components` tag.css.js:58-106) maps each
    // of the 8 Tractie colors to `--tractie-tag-background-color` via
    // `:host(tractie-tag[color='X'])`. Yellow additionally remaps fg to
    // `--tractie-color-content-on-accent-yellow-default` (dark text on
    // bright yellow bg) for WCAG contrast. All other colors keep the
    // default white fg from the root.
    //
    // MUI v4's `color` prop API is fixed (default/primary/secondary), so
    // we surface the full 8-color palette via the `data-tractie-color`
    // attribute spread by consumers (zie ns-tag.comparison.stories.tsx,
    // catalog rendering — same convention as the BUI side, T4 in
    // bui-tag-overrides.css). All bg-tokens are mode-aware via tokens.css.
    // ────────────────────────────────────────────────────────────────────
    // Note on fg: Tractie's color modifiers ONLY override bg for non-yellow
    // colors (see tag.css.js:58-87,95-104). fg blijft `content-default-inverse`
    // = `--ns-color-fg-default-inverse`. Bij MUI Chip wordt `colorPrimary`
    // (geactiveerd via `color="primary"` in `muiColorFor('blue')`) echter
    // óók fg overschreven naar `--ns-color-fg-interactive-default-inverse`.
    // In dark-mode resolveert dat tot een ander rgb dan `fg-default-inverse`,
    // wat in cluster 2 zichtbare divergentie veroorzaakt op de blue tag.
    // Daarom pinnen we fg expliciet in elke kleur-bridge regel — matches
    // Tractie exact en defeat de colorPrimary/colorSecondary fg-override.
    '&[data-tractie-color="gray"]': {
      backgroundColor: 'var(--ns-color-bg-accent-gray-default)',
      color: 'var(--ns-color-fg-default-inverse)',
    },
    '&[data-tractie-color="blue"]': {
      backgroundColor: 'var(--ns-color-bg-accent-blue-default)',
      color: 'var(--ns-color-fg-default-inverse)',
    },
    '&[data-tractie-color="green"]': {
      backgroundColor: 'var(--ns-color-bg-accent-green-default)',
      color: 'var(--ns-color-fg-default-inverse)',
    },
    '&[data-tractie-color="orange"]': {
      backgroundColor: 'var(--ns-color-bg-accent-orange-default)',
      color: 'var(--ns-color-fg-default-inverse)',
    },
    '&[data-tractie-color="red"]': {
      backgroundColor: 'var(--ns-color-bg-accent-red-default)',
      color: 'var(--ns-color-fg-default-inverse)',
    },
    // Yellow: Tractie spec remaps both bg and fg (dark text on bright
    // yellow for WCAG AA — tag.css.js:88-90). Parity vereist
    // on-accent-yellow-default token (donker label).
    '&[data-tractie-color="yellow"]': {
      backgroundColor: 'var(--ns-color-bg-accent-yellow-default)',
      color: 'var(--ns-color-fg-on-accent-yellow-default)',
    },
    '&[data-tractie-color="purple"]': {
      backgroundColor: 'var(--ns-color-bg-accent-purple-default)',
      color: 'var(--ns-color-fg-default-inverse)',
    },
    '&[data-tractie-color="pink"]': {
      backgroundColor: 'var(--ns-color-bg-accent-pink-default)',
      color: 'var(--ns-color-fg-default-inverse)',
    },
    // ────────────────────────────────────────────────────────────────────
    // Cluster 3 (subtle): Tractie subtle-appearance color-bridge.
    //
    // Per @tractie/components tag.css.js:61-111, `[appearance='subtle']`
    // changes both bg (to accent-X-subtle) and fg (to content-on-accent-
    // subtle = dark text for WCAG on light/pastel bg). Specificity analysis:
    //   - `[color='X'][appearance='subtle']` (2 attrs) overrides bg
    //   - universal `[appearance='subtle']` (1 attr, later) overrides fg
    //     (yellow's `[color='yellow']` fg-override is also 1 attr; source
    //     order makes universal subtle win for fg on yellow too).
    //
    // Net result for ALL 8 colors in subtle: bg = accent-X-subtle, fg =
    // fg-on-accent-subtle. No yellow exception in subtle.
    //
    // We pin fg per rule to defeat MUI colorPrimary/colorSecondary fg
    // overrides in dark-mode (same defense as solid cluster).
    // ────────────────────────────────────────────────────────────────────
    '&[data-tractie-color="gray"][data-tractie-appearance="subtle"]': {
      backgroundColor: 'var(--ns-color-bg-accent-gray-subtle)',
      color: 'var(--ns-color-fg-on-accent-subtle)',
    },
    '&[data-tractie-color="blue"][data-tractie-appearance="subtle"]': {
      backgroundColor: 'var(--ns-color-bg-accent-blue-subtle)',
      color: 'var(--ns-color-fg-on-accent-subtle)',
    },
    '&[data-tractie-color="green"][data-tractie-appearance="subtle"]': {
      backgroundColor: 'var(--ns-color-bg-accent-green-subtle)',
      color: 'var(--ns-color-fg-on-accent-subtle)',
    },
    '&[data-tractie-color="orange"][data-tractie-appearance="subtle"]': {
      backgroundColor: 'var(--ns-color-bg-accent-orange-subtle)',
      color: 'var(--ns-color-fg-on-accent-subtle)',
    },
    '&[data-tractie-color="red"][data-tractie-appearance="subtle"]': {
      backgroundColor: 'var(--ns-color-bg-accent-red-subtle)',
      color: 'var(--ns-color-fg-on-accent-subtle)',
    },
    '&[data-tractie-color="yellow"][data-tractie-appearance="subtle"]': {
      backgroundColor: 'var(--ns-color-bg-accent-yellow-subtle)',
      color: 'var(--ns-color-fg-on-accent-subtle)',
    },
    '&[data-tractie-color="purple"][data-tractie-appearance="subtle"]': {
      backgroundColor: 'var(--ns-color-bg-accent-purple-subtle)',
      color: 'var(--ns-color-fg-on-accent-subtle)',
    },
    '&[data-tractie-color="pink"][data-tractie-appearance="subtle"]': {
      backgroundColor: 'var(--ns-color-bg-accent-pink-subtle)',
      color: 'var(--ns-color-fg-on-accent-subtle)',
    },
    // ────────────────────────────────────────────────────────────────────
    // Cluster C3 — size sampler (xs/s/m/l) — Tractie-strict.
    //
    // Tractie spec (`@tractie/components` tag.css.js:30-56) defines only
    // `m` (default: 22px height, 12px font, 2/8 padding) and `l` (large:
    // 29px height, 14px font, 4/12 padding). Tractie's eigen size-sampler
    // toont xs/s/m identiek aan m-spec (geen step-down voor sub-m sizes);
    // alleen `l` schaalt op. We volgen die strikte Tractie-bias hier:
    //
    //   xs → identiek aan m (22h / 12fs / 2-8 padding)
    //   s  → identiek aan m (22h / 12fs / 2-8 padding)
    //   m  → Tractie default (22h / 12fs / 2-8 padding)
    //   l  → Tractie size='l' spec (29h / 14fs / 4-12 padding)
    //
    // Height wordt expliciet gezet om MUI's eigen `MuiChip-sizeSmall`
    // height-shrink (15-18.5px voor size="small") te overrulen — anders
    // krijgen consumers die size="small" doorgeven (om gap met BUI te
    // dichten) automatisch een kleinere chip dan Tractie voorschrijft.
    //
    // Activated via `data-tractie-size="X"` attribute spread by consumers.
    // ────────────────────────────────────────────────────────────────────
    '&[data-tractie-size="xs"]': {
      height: '22px',
      fontSize: '0.75rem',
      padding: '2px 8px',
    },
    '&[data-tractie-size="s"]': {
      height: '22px',
      fontSize: '0.75rem',
      padding: '2px 8px',
    },
    '&[data-tractie-size="m"]': {
      height: '22px',
      fontSize: '0.75rem',
      padding: '2px 8px',
    },
    '&[data-tractie-size="l"]': {
      height: '29px',
      fontSize: '0.875rem',
      padding: '4px 12px',
    },
    // ────────────────────────────────────────────────────────────────────
    // Cluster C4 — inline icon spacing.
    //
    // Tractie spec (`tag.css.js:113-115`) switches display to inline-flex
    // when a `tractie-icon` is present and applies
    // `--tractie-tag-gap-size: var(--tractie-spacing-spacer-0-5)` (4px) via
    // the styles layer (`gap: var(--_gap-size)`).
    //
    // Root already declares `display: 'inline-flex'` and `alignItems:
    // 'center'`. We add an explicit gap so child icons get consistent
    // spacing whether or not the consumer wraps in a flex container.
    // ────────────────────────────────────────────────────────────────────
    gap: '4px',
    // ────────────────────────────────────────────────────────────────────
    // Cluster C5/C6/C7 — NS-specific modifiers (data-ns-*).
    //
    // Mirror the BUI Tag T5a/T5b/T5c rules so MUI Chip-as-Tag consumers
    // can reach the same NS modifiers via attribute-hook.
    //
    //   data-ns-outlined        → transparent fill + interactive border
    //   data-ns-variant=primary → solid interactive fill
    //   data-ns-variant=secondary → Tractie subtle blue fill (C7 fix)
    // ────────────────────────────────────────────────────────────────────
    // C5 — outlined modifier (Tractie-spirit, color-aware).
    //
    // Tractie defineert geen `outlined` modifier expliciet; we deriven 'm uit
    // het principe "tag-color als border + tag-color als fg, transparante fill".
    // Per kleur reflecteert outlined dezelfde --ns-color-bg-accent-X-default
    // als de solid-variant gebruikt voor zijn fill (single source-of-truth
    // per palette-kleur).
    //
    // Fallback (geen data-tractie-color): gray — Tractie-spirit default-ghost.
    '&[data-ns-outlined]': {
      backgroundColor: 'transparent',
      border: '1px solid var(--ns-color-bg-accent-gray-default)',
      color: 'var(--ns-color-bg-accent-gray-default)',
    },
    '&[data-ns-outlined][data-tractie-color="gray"]': {
      backgroundColor: 'transparent',
      borderColor: 'var(--ns-color-bg-accent-gray-default)',
      color: 'var(--ns-color-bg-accent-gray-default)',
    },
    '&[data-ns-outlined][data-tractie-color="blue"]': {
      backgroundColor: 'transparent',
      borderColor: 'var(--ns-color-bg-accent-blue-default)',
      color: 'var(--ns-color-bg-accent-blue-default)',
    },
    '&[data-ns-outlined][data-tractie-color="green"]': {
      backgroundColor: 'transparent',
      borderColor: 'var(--ns-color-bg-accent-green-default)',
      color: 'var(--ns-color-bg-accent-green-default)',
    },
    '&[data-ns-outlined][data-tractie-color="orange"]': {
      backgroundColor: 'transparent',
      borderColor: 'var(--ns-color-bg-accent-orange-default)',
      color: 'var(--ns-color-bg-accent-orange-default)',
    },
    '&[data-ns-outlined][data-tractie-color="red"]': {
      backgroundColor: 'transparent',
      borderColor: 'var(--ns-color-bg-accent-red-default)',
      color: 'var(--ns-color-bg-accent-red-default)',
    },
    '&[data-ns-outlined][data-tractie-color="yellow"]': {
      backgroundColor: 'transparent',
      borderColor: 'var(--ns-color-bg-accent-yellow-default)',
      color: 'var(--ns-color-bg-accent-yellow-default)',
    },
    '&[data-ns-outlined][data-tractie-color="purple"]': {
      backgroundColor: 'transparent',
      borderColor: 'var(--ns-color-bg-accent-purple-default)',
      color: 'var(--ns-color-bg-accent-purple-default)',
    },
    '&[data-ns-outlined][data-tractie-color="pink"]': {
      backgroundColor: 'transparent',
      borderColor: 'var(--ns-color-bg-accent-pink-default)',
      color: 'var(--ns-color-bg-accent-pink-default)',
    },
    '&[data-ns-variant="primary"]': {
      backgroundColor: 'var(--ns-color-bg-interactive-default)',
      borderColor: 'var(--ns-color-bg-interactive-default)',
      color:
        'var(--ns-color-fg-on-interactive, var(--ns-color-fg-default-inverse))',
    },
    '&[data-ns-variant="secondary"]': {
      // C7 fix: variant=secondary mapt op Tractie subtle blue (zelfde tokens
      // als C2 Subtle cluster, bg=accent-blue-subtle, fg=fg-on-accent-subtle).
      // Eerdere mapping op --bui-bg-info / info-tokens gaf een licht-info-bg
      // met interactive-blue fg, wat afweek van de Tractie subtle-look.
      backgroundColor: 'var(--ns-color-bg-accent-blue-subtle)',
      borderColor: 'var(--ns-color-bg-accent-blue-subtle)',
      color: 'var(--ns-color-fg-on-accent-subtle)',
    },
  },

  sizeSmall: {
    height: 'auto',
  },

  colorPrimary: {
    backgroundColor: 'var(--ns-color-bg-interactive-default)',
    color: 'var(--ns-color-fg-interactive-default-inverse)',
  },

  colorSecondary: {
    backgroundColor: 'var(--ns-color-bg-brand-secondary)',
    color: 'var(--ns-color-fg-default-inverse)',
  },

  disabled: {},

  clickable: {
    userSelect: 'none' as const,
    WebkitTapHighlightColor: 'transparent',
    cursor: 'pointer' as const,
    transition: 'none',
  },

  clickableColorPrimary: {
    transition: 'none',
  },

  clickableColorSecondary: {
    transition: 'none',
  },

  deletable: {
    transition: 'none',
  },

  deletableColorPrimary: {},

  deletableColorSecondary: {},

  outlined: {
    // Backstage's CatalogTable Tags column renders MUI v4 chips with
    // `variant="outlined"` and no `color` prop. Per Tractie tag spec
    // (`@tractie/components` tag.css.js, `[color='blue']` variant) we map
    // MUI's outlined-default to Tractie's accent-blue (data-blue) tag:
    // solid accent-blue bg + white inverse text, no border. This is the
    // *Tractie* blue (palette/data-visualisation), not the *NS UI*
    // interactive-blue (CTA/links). Other Tractie tag colors (gray, green,
    // orange, red, yellow, purple, pink) are reachable via BUI Tag with
    // `data-color="X"` (see bui-tag-overrides.css T4); MUI v4 Chip's
    // `color` prop API is fixed (default/primary/secondary) and cannot be
    // extended to accent-* without forking the component, so the BUI side
    // is the only route for full color coverage.
    backgroundColor: 'var(--ns-color-bg-accent-blue-default)',
    color: 'var(--ns-color-fg-default-inverse)',
    border: 'none',
    '&$clickable:hover, &$clickable:focus, &$deletable:focus': {
      // No accent-blue-hover token exists; re-use the default token to
      // preserve the Tractie palette. UI interactive-hover is the wrong
      // token here (would re-introduce the UI-blue → Tractie-blue mismatch
      // on hover).
      backgroundColor: 'var(--ns-color-bg-accent-blue-default)',
    },
  },

  outlinedPrimary: {
    color: 'var(--ns-color-fg-interactive-default)',
    border: '1px solid var(--ns-color-border-interactive-default)',
  },

  outlinedSecondary: {
    color: 'var(--ns-color-fg-emphasis)',
    border: '1px solid var(--ns-color-fg-emphasis)',
  },

  avatar: {
    marginLeft: 4,
    marginRight: -4,
    width: 24,
    height: 24,
    color: 'inherit' as const,
    fontSize: '0.75rem',
  },

  avatarSmall: {
    marginLeft: 2,
    marginRight: -4,
    width: 18,
    height: 18,
    fontSize: '0.625rem',
  },

  avatarColorPrimary: {},

  avatarColorSecondary: {},

  icon: {
    color: 'inherit' as const,
    marginLeft: 4,
    marginRight: -8,
  },

  iconSmall: {
    width: 18,
    height: 18,
    marginLeft: 2,
    marginRight: -6,
  },

  iconColorPrimary: {
    color: 'inherit' as const,
  },

  iconColorSecondary: {
    color: 'inherit' as const,
  },

  label: {
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    // Tractie tag has no inner-label inset; all horizontal padding comes from
    // the root (--tractie-tag-padding-inline = spacer-2 = 8px). MUI Chip's
    // default `MuiChip-label` paddingLeft/Right=12px would compound with the
    // root 8px, inflating the chip width by 24px vs the Tractie pendant.
    // Setting label padding to 0 collapses MUI to the Tractie inset.
    paddingLeft: 0,
    paddingRight: 0,
    whiteSpace: 'nowrap' as const,
  },

  labelSmall: {
    paddingLeft: 0,
    paddingRight: 0,
  },

  deleteIcon: {
    WebkitTapHighlightColor: 'transparent',
    color: 'var(--ns-color-fg-default)',
    height: 22,
    width: 22,
    cursor: 'pointer' as const,
    margin: '0 5px 0 -6px',
    '&:hover': {
      color: 'var(--ns-color-fg-emphasis)',
    },
  },

  deleteIconSmall: {
    height: 16,
    width: 16,
    marginRight: 4,
    marginLeft: -4,
  },

  deleteIconColorPrimary: {
    color: 'inherit' as const,
  },

  deleteIconColorSecondary: {
    color: 'inherit' as const,
  },

  deleteIconOutlinedColorPrimary: {
    color: 'var(--ns-color-fg-interactive-default)',
  },

  deleteIconOutlinedColorSecondary: {
    color: 'var(--ns-color-fg-emphasis)',
  },
} as const;

// Dark variant — alle kleur-tokens zijn mode-aware via tokens.css.
export const MuiChipDarkOverrides = {
  ...MuiChipLightOverrides,
} as const;
